import { useMutation, useQuery } from '@apollo/client'
import { ALL_FIXED_SALARY_ADDONS } from './queries'
import { useConfirm, useNodes, usePermissions } from '@/utils/hooks'
import { formatISO, parseISO } from 'date-fns'
import { usePrompt, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { FixedSalaryAddonNode } from './types.graphql'
import {
  CREATE_FIXED_SALARY_ADDON,
  DELETE_FIXED_SALARY_ADDON,
  PATCH_FIXED_SALARY_ADDON,
} from './mutations'
import { PERMISSIONS } from '@/utils/permissions'
import type { ConfigureFixedAddonPromptForm } from './ConfigureFixedAddonPrompt'
import { ConfigureFixedAddonPrompt } from './ConfigureFixedAddonPrompt'
import { FixedAddonList } from './fixed-addon-list'

interface FixedSalaryAddonsProps {
  typeId: string
  type: 'user' | 'salaryWorkTime'
}

export const FixedSalaryAddons: React.JSX.CFC<FixedSalaryAddonsProps> = ({
  typeId,
  type,
}) => {
  const translations = useTranslate({
    hourlyOrFixedAddon: 'salary.fixed-salary-addons',
    overrideOtherAddons: 'salary.override-other-addons',

    start: 'common.start',
    end: 'common.end',
    salaryCode: 'common.salary-code',
    edit: 'common.edit',
    delete: 'common.delete',

    deleteTimeOverride: 'salary.delete-time-addon',
    deleteTimeOverrideConfirm: 'salary.delete-time-addon-confirm',

    toast: {
      successCreate: 'salary.time-addon-create-success',
      successUpdate: 'salary.time-addon-update-success',
      successDelete: 'salary.time-addon-delete-success',
    },
  })

  const addPrompt = usePrompt()
  const addToast = useToast()
  const confirm = useConfirm()
  const { hasPermissions } = usePermissions()

  const canAdd = hasPermissions(PERMISSIONS.salary.add.timeaddon, true)

  const canEdit = hasPermissions(PERMISSIONS.salary.change.timeaddon, true)
  const canDelete = hasPermissions(PERMISSIONS.salary.delete.timeaddon, true)

  const { data, loading } = useQuery(ALL_FIXED_SALARY_ADDONS, {
    variables: {
      [type]: typeId,
    },
  })

  const refetchQueries = [
    {
      query: ALL_FIXED_SALARY_ADDONS,
      variables: {
        [type]: typeId,
      },
    },
    'DriverActivityPeriodReport',
    'DriverActivityPeriodReportShallow',
    'AllDynamicColumnsQuery',
    'DriverActivityDayReport',
  ]

  const [createFixedSalaryAddon] = useMutation(CREATE_FIXED_SALARY_ADDON, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successCreate)
    },
  })

  const [patchFixedSalaryAddon] = useMutation(PATCH_FIXED_SALARY_ADDON, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successUpdate)
    },
  })

  const [deleteFixedSalaryAddon] = useMutation(DELETE_FIXED_SALARY_ADDON, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successDelete)
    },
  })

  const fixedSalaryAddons = useNodes(data?.allFixedSalaryAddons)

  const handleAdd = async () => {
    const initialData = {
      salaryCode: null,
      salaryCodeIfOvertime: null,
      validFrom: null,
      validTo: null,
    }
    const { data } = await addPrompt<ConfigureFixedAddonPromptForm | null>(
      resolve => (
        <ConfigureFixedAddonPrompt
          initialData={initialData}
          onSubmit={resolve}
        />
      )
    )

    if (!data) return

    let patchData = {}

    if (data.validFrom && data.validTo) {
      patchData = {
        validFrom: formatISO(data.validFrom),
        validTo: formatISO(data.validTo),
      }
    } else if (data.validFrom) {
      patchData = {
        validFrom: formatISO(data.validFrom),
      }
    } else if (data.validTo) {
      patchData = {
        validTo: formatISO(data.validTo),
      }
    }
    void createFixedSalaryAddon({
      variables: {
        input: {
          ...patchData,
          salaryCode: data.salaryCode ?? '',
          [type]: typeId,
        },
      },
    })
  }

  const handleEdit = async (addon: FixedSalaryAddonNode) => {
    const initialData = {
      salaryCode: addon.salaryCode?.id ?? '',
      salaryCodeIfOvertime: null,
      validFrom: addon.validFrom ? parseISO(addon.validFrom) : null,
      validTo: addon.validTo ? parseISO(addon.validTo) : null,
    }
    const { data } = await addPrompt<ConfigureFixedAddonPromptForm | null>(
      resolve => (
        <ConfigureFixedAddonPrompt
          initialData={initialData}
          editing
          onSubmit={resolve}
        />
      )
    )

    if (!data) return

    let datetimeData = {}

    if (data.validFrom && data.validTo) {
      datetimeData = {
        validFrom: formatISO(data.validFrom),
        validTo: formatISO(data.validTo),
      }
    } else if (data.validFrom) {
      datetimeData = {
        validFrom: formatISO(data.validFrom),
      }
    } else if (data.validTo) {
      datetimeData = {
        validTo: formatISO(data.validTo),
      }
    }

    void patchFixedSalaryAddon({
      variables: {
        id: addon.id,
        input: {
          ...datetimeData,
          salaryCode: data.salaryCode,
        },
      },
    })
  }

  const handleDelete = async (addon: FixedSalaryAddonNode) => {
    const { data } = await confirm(
      translations.deleteTimeOverrideConfirm,
      translations.deleteTimeOverride
    )

    if (!data) return

    void deleteFixedSalaryAddon({
      variables: {
        id: addon.id,
      },
    })
  }

  return (
    <FixedAddonList
      title={translations.hourlyOrFixedAddon}
      addons={fixedSalaryAddons}
      loading={loading}
      canAdd={canAdd}
      canEdit={canEdit}
      canDelete={canDelete}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  )
}
